<template>
  <div class="col-12 mb-2">
    <h5 class="mb-1">{{ $t('direct_debit_mandates.mandate_section') }}</h5>
  </div>

  <div class="mb-3 col-12 col-lg-5">
    <div class="form-floating">
      <Field id="creditor_account_id" name="creditor_account_id" as="select" class="form-select" :class="{'is-invalid': errors.creditor_account_id}">
        <option value="">{{ $t('direct_debit_mandates.creditor_account_blank') }}</option>
        <option v-for="account in accountStore.accounts" :key="account.id" :value="account.id">
          {{ accountLabel(account) }}
        </option>
      </Field>
      <label for="creditor_account_id" class="form-label">{{ $t('direct_debit_mandates.creditor_account_id') }}</label>
      <div v-if="errors.creditor_account_id" class="invalid-feedback">
        {{ errors.creditor_account_id }}
      </div>
    </div>
  </div>
  <div class="mb-3 col-12 col-lg-3">
    <div class="form-floating">
      <Field id="mandate_date" name="mandate_date" class="form-control" :class="{'is-invalid': errors.mandate_date}" type="date" />
      <label for="mandate_date" class="form-label">{{ $t('direct_debit_mandates.mandate_date') }}</label>
      <div v-if="errors.mandate_date" class="invalid-feedback">
        {{ errors.mandate_date }}
      </div>
    </div>
  </div>
  <div class="mb-3 col-12 col-lg-4">
    <div class="form-floating">
      <Field id="reference" name="reference" class="form-control" :class="{'is-invalid': errors.reference}" :disabled="createMode"/>
      <label for="reference" class="form-label">{{ $t('direct_debit_mandates.reference') }}</label>
      <div v-if="errors.reference" class="invalid-feedback">
        {{ errors.reference }}
      </div>
    </div>
  </div>

  <div class="col-12 mt-2 mb-2">
    <h5 class="mb-1">{{ $t('direct_debit_mandates.debtor_section') }}</h5>
  </div>

  <div class="mb-3 col-12 col-lg-4">
    <div class="form-floating">
      <Field id="account_owner" name="account_owner" class="form-control" :class="{'is-invalid': errors.account_owner}"/>
      <label for="account_owner" class="form-label">{{ $t('direct_debit_mandates.account_owner') }}</label>
      <div v-if="errors.account_owner" class="invalid-feedback">
        {{ errors.account_owner }}
      </div>
    </div>
  </div>
  <div class="mb-3 col-12 col-lg-4">
    <div class="form-floating">
      <Field id="iban" name="iban" class="form-control" :class="{'is-invalid': errors.iban}"/>
      <label for="iban" class="form-label">{{ $t('direct_debit_mandates.iban') }}</label>
      <div v-if="errors.iban" class="invalid-feedback">
        {{ errors.iban }}
      </div>
    </div>
  </div>
  <div class="mb-3 col-12 col-lg-2">
    <div class="form-floating">
      <Field id="bic" name="bic" class="form-control" :class="{'is-invalid': errors.bic}"/>
      <label for="bic" class="form-label">{{ $t('direct_debit_mandates.bic') }}</label>
      <div v-if="errors.bic" class="invalid-feedback">
        {{ errors.bic }}
      </div>
    </div>
  </div>
  <div class="mb-3 col-12 col-lg-2">
    <div class="form-floating">
      <Field id="bank_name" name="bank_name" class="form-control" :class="{'is-invalid': errors.bank_name}"/>
      <label for="bank_name" class="form-label">{{ $t('direct_debit_mandates.bank_name') }}</label>
      <div v-if="errors.bank_name" class="invalid-feedback">
        {{ errors.bank_name }}
      </div>
    </div>
  </div>

  <div class="col-12 mt-2 mb-2">
    <h5 class="mb-1">{{ $t('direct_debit_mandates.validity_section') }}</h5>
  </div>

  <div class="mb-3 col-12 col-lg-4">
    <Field v-slot="{ field }" name="recurring_payments" >
      <label class="form-check d-flex gap-2 align-items-center">
        <input type="checkbox" class="form-check-input mt-0" name="recurring_payments" @change="setFieldValue('recurring_payments', field.value)" v-model="field.value" :value="true" />
        <span class="form-check-label">{{ $t('direct_debit_mandates.recurring_payments') }}</span>
      </label>
    </Field>
  </div>
  <div class="mb-3 col-12 col-lg-4">
    <Field v-slot="{ field }" name="one_time_payments" >
      <label class="form-check d-flex gap-2 align-items-center">
        <input type="checkbox" class="form-check-input mt-0" name="one_time_payments" @change="setFieldValue('one_time_payments', field.value)" v-model="field.value" :value="true" />
        <span class="form-check-label">{{ $t('direct_debit_mandates.one_time_payments') }}</span>
      </label>
    </Field>
  </div>
  <div class="mb-3 col-12 col-lg-4">
    <div class="form-floating">
      <Field id="end_date" name="end_date" class="form-control" :class="{'is-invalid': errors.end_date}" type="date"/>
      <label for="end_date" class="form-label">{{ $t('direct_debit_mandates.end_date') }}</label>
      <div v-if="errors.end_date" class="invalid-feedback">
        {{ errors.end_date }}
      </div>
    </div>
  </div>

  <div class="col-12 mt-2 mb-2">
    <h5 class="mb-1">{{ $t('direct_debit_mandates.document_section') }}</h5>
  </div>

  <div class="mb-3 col-12 col-lg-3" v-if="ddm">
    <div class="d-flex flex-column direct-debit-mandate-preview">
      <vue-pdf-embed :page="1" :source="ddm.file_url" v-if="ddm.file_type === 'application/pdf'"/>
      <img class="w-100" :src="ddm.file_url" v-if="ddm.file_type === 'image/jpeg'" />
    </div>
  </div>
  <div class="mb-3 col-12 col-lg-4" v-if="ddm?.file_type">
    <div class="alert alert-warning mb-0 h-100">
      {{ $t('direct_debit_mandates.upload_warning') }}
    </div>
  </div>
  <div class="mb-3 col-12" :class="ddm ? 'col-lg-5' : ''">
    <Field name="file" v-slot="{ handleChange, handleBlur }">
      <DropZone class="col-12" :class="{'is-invalid': errors.file}" :show-buttons="false" :accept-files="['.pdf', '.jpg']" @change="handleChange" @blur="handleBlur"/>
      <div v-if="errors.file" class="invalid-feedback">
        {{ errors.file }}
      </div>
    </Field>
  </div>
  <Field type="hidden" id="client_id" name="client_id" :value="client.id" />
  <Field type="hidden" id="file_url" name="file_url" />
</template>

<script>
import {Field} from "vee-validate";
import DropZone from "@/components/DropZone.vue";
import VuePdfEmbed from 'vue-pdf-embed'
import {useAccountStore} from "@/stores/account";

export default {
  name: 'DirectDebitMandateFormFields',
  components: {
    Field,
    DropZone,
    VuePdfEmbed
  },
  props: {
    errors: {
      type: Object,
      default: () => {
      },
    },
    createMode: {
      type: Boolean,
      default: false
    },
    ddm: {
      type: Object,
      default: () => null,
    },
    client: {
      type: Object
    },
    setFieldValue: {
      type: Function,
      default: () => {
      },
    },
  },
  setup() {
    return {
      accountStore: useAccountStore(),
    }
  },
  data() {
    return {
    }
  },
  created() {
    this.accountStore.getAccounts()
  },
  computed: {
  },
  methods: {
    accountLabel(account) {
      return [account.account_name || account.name || account.bank_name, account.iban].filter(Boolean).join(' - ')
    },
  }
}
</script>

<style scoped>
.direct-debit-mandate-preview {
  max-width: 16rem;
}
</style>
